import { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translateEmotionCategory } from '../translations';
import { HOFFMAN_CATEGORIES, getCategoryNames } from '../hoffmanModel';

export default function EmotionGrid({ onCategorySelect }) {
  const { t, language } = useLanguage();
  const categories = getCategoryNames();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  // Sort categories by valence
  const sortedCategories = [...categories].sort((a, b) => {
    return HOFFMAN_CATEGORIES[b].valence - HOFFMAN_CATEGORIES[a].valence;
  });

  const currentCategory = sortedCategories[selectedIndex];
  const categoryData = HOFFMAN_CATEGORIES[currentCategory];

  const handleSelect = () => {
    onCategorySelect(currentCategory);
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev + 1) % sortedCategories.length);
  };

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev - 1 + sortedCategories.length) % sortedCategories.length);
  };

  // Touch/Swipe handlers - Allow tap, prevent swipe scroll
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    // Don't preventDefault here - allows tap to work
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    
    // Only prevent default if horizontal swipe detected
    if (touchStartX.current) {
      const diff = Math.abs(touchStartX.current - e.touches[0].clientX);
      if (diff > 10) { // Horizontal movement detected
        e.preventDefault(); // Prevent screen scroll only during swipe
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current || !touchEndX.current) {
      // No swipe - let click handler work
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    
    if (Math.abs(diff) > threshold) {
      // Swipe detected - prevent click
      e.preventDefault();
      e.stopPropagation();
      
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    // else: Small movement or tap - let onClick handle it
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-4" style={{ touchAction: 'pan-y' }}>
      {/* Swipeable Emotion Card - Responsive height */}
      <div 
        onClick={handleSelect}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="rounded-3xl border-4 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95"
        style={{
          width: 'min(300px, 85vw)',
          height: 'min(380px, 55vh)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 24px',
          borderColor: categoryData.color,
          background: `linear-gradient(135deg, ${categoryData.color}20 0%, white 100%)`,
          boxShadow: `0 20px 40px ${categoryData.color}40, 0 8px 16px rgba(0,0,0,0.15)`,
          touchAction: 'manipulation',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none'
        }}
      >
        {/* Color circle */}
        <div
          style={{
            width: '100px',
            height: '100px',
            minWidth: '100px',
            minHeight: '100px',
            borderRadius: '50%',
            marginBottom: '16px',
            flexShrink: 0,
            backgroundColor: categoryData.color,
            boxShadow: `0 16px 40px ${categoryData.color}60, 0 8px 16px rgba(0,0,0,0.1)`
          }}
        />

        {/* Category name */}
        <div 
          style={{
            height: '70px',
            minHeight: '70px',
            maxHeight: '70px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '10px',
            overflow: 'hidden'
          }}
        >
          <h2
            className="font-bold text-warm-charcoal text-center leading-tight"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              fontSize: '22px',
              padding: '0 4px'
            }}
          >
            {translateEmotionCategory(currentCategory, language)}
          </h2>
        </div>

        {/* Feeling count */}
        <p className="text-xs text-warm-charcoal/60" style={{ marginBottom: '10px' }}>
          {t('specific_feelings').replace('{count}', categoryData.feelings.length)}
        </p>

        {/* Valence badge */}
        <div 
          className={`rounded-full text-xs font-semibold ${
            categoryData.valence > 0 ? 'bg-green-100 text-green-700' : categoryData.valence < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
          }`}
          style={{ padding: '6px 16px', marginBottom: '16px' }}
        >
          {categoryData.valence > 0 ? t('positive') : categoryData.valence < 0 ? t('challenging') : t('neutral')}
        </div>

        {/* Select button */}
        <button
          onClick={handleSelect}
          className="text-white font-bold rounded-2xl transition-all duration-300 hover:scale-105"
          style={{
            width: '100%',
            height: '46px',
            fontSize: '15px',
            backgroundColor: categoryData.color,
            boxShadow: `0 12px 24px ${categoryData.color}60, 0 6px 12px rgba(0,0,0,0.15)`
          }}
        >
          {t('select_this_category')}
        </button>
      </div>

      {/* Navigation arrows */}
      <div className="flex items-center justify-center gap-4" style={{ marginTop: '12px' }}>
        <button
          onClick={handlePrev}
          className="rounded-full bg-white shadow-md hover:shadow-lg hover:bg-hunter-green hover:text-white transition-colors flex items-center justify-center font-light"
          style={{ width: '44px', height: '44px', fontSize: '24px', touchAction: 'manipulation' }}
        >
          ‹
        </button>
        
        <p className="text-xs text-warm-charcoal/40 italic">
          Swipe or tap arrows
        </p>

        <button
          onClick={handleNext}
          className="rounded-full bg-white shadow-md hover:shadow-lg hover:bg-hunter-green hover:text-white transition-colors flex items-center justify-center font-light"
          style={{ width: '44px', height: '44px', fontSize: '24px', touchAction: 'manipulation' }}
        >
          ›
        </button>
      </div>
    </div>
  );
}
